"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

// Meta's official "standard events". fbq('track', ...) is reserved for these;
// anything else must use fbq('trackCustom', ...) or Meta logs a console
// warning ("non-standard event") and may discard the event.
// https://developers.facebook.com/docs/meta-pixel/reference#standard-events
const STANDARD_EVENTS = new Set([
  "AddPaymentInfo",
  "AddToCart",
  "AddToWishlist",
  "CompleteRegistration",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "InitiateCheckout",
  "Lead",
  "PageView",
  "Purchase",
  "Schedule",
  "Search",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
  "ViewContent",
]);

export function pixelTrack(
  event: string,
  params?: Record<string, unknown>,
  eventId?: string
) {
  if (typeof window === "undefined" || !window.fbq) return;
  const options = eventId ? { eventID: eventId } : {};
  const method = STANDARD_EVENTS.has(event) ? "track" : "trackCustom";
  window.fbq(method, event, params ?? {}, options);
}

export default function MetaPixelTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialized = useRef(false);

  useEffect(() => {
    if (!window.fbq) return;
    if (!initialized.current) {
      initialized.current = true;
      return; // layout already fired the initial PageView via fbq('init')
    }
    window.fbq("track", "PageView");
  }, [pathname, searchParams]);

  return null;
}
